import React from "react";
import { Box, Typography, Button, Alert, alpha, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify/react";
import StyledTextArea from "../../../components/Fields/TextArea";
import StyledMultiSelect from "../../../components/Fields/MultiSelect";
import MultipleFilesSection from "./MultipleFilesSection";
import type { FormProps } from "../types";
import type { UserDocumentResponse } from "../../../services";
import type { CompanyRuleDto } from "../../../services/configurationService";

interface CompanyStandardFormProps extends FormProps {
  customInstructions: string;
  onCustomInstructionsChange: (value: string) => void;
  companyRules: CompanyRuleDto[];
  selectedRuleIds: string[];
  onSelectedRuleIdsChange: (ids: string[]) => void;
  rulesLoading: boolean;
  noRulesError: string | null;
  selectedCompanyId?: string;
  multipleFiles: File[];
  multipleSelectedDocuments: UserDocumentResponse[];
  onFileRemove: (index: number) => void;
  onDocumentDeselect: (documentId: string) => void;
  onFileUpload: (files: FileList) => void;
  onClearAll: () => void;
  onDownloadDocument: (document: UserDocumentResponse) => void;
}

const CompanyStandardForm: React.FC<CompanyStandardFormProps> = ({
  customInstructions,
  onCustomInstructionsChange,
  companyRules,
  selectedRuleIds,
  onSelectedRuleIdsChange,
  rulesLoading,
  noRulesError,
  multipleFiles,
  multipleSelectedDocuments,
  onFileRemove,
  onDocumentDeselect,
  onFileUpload,
  onClearAll,
  onDownloadDocument,
  isProcessing,
  selectedCompanyId,
  isSmallScreen,
  onOpenBottomDrawer,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  // Supporting copy sits on the navy config panel on desktop: text.secondary is
  // far below 4.5:1 there (UI/UX audit 2.4.1).
  const bodyColor = isSmallScreen
    ? theme.palette.text.secondary
    : alpha(theme.palette.common.white, 0.85);

  return (
    <>
      {/* Company Rules Multi-Select */}
      {selectedCompanyId && (
        <Box>
          <Typography
            variant="h6"
            sx={{
              color: isSmallScreen
                ? theme.palette.text.primary
                : theme.palette.common.white,
              fontWeight: 600,
              fontSize: "16px",
              mb: 1,
            }}
          >
            {t("functionalities.form.selectCompanyRules")}
          </Typography>
          <Typography variant="body2" color={bodyColor} sx={{ mb: 2 }}>
            {t("functionalities.form.selectCompanyRulesHint")}
          </Typography>

          {/* Show error message if no rules are available */}
          {noRulesError && !rulesLoading && (
            <Alert
              severity="warning"
              sx={{
                mb: 2,
                backgroundColor: alpha(theme.palette.warning.main, 0.1),
                border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                color: isSmallScreen
                  ? theme.palette.warning.dark
                  : theme.palette.warning.light,
                "& .MuiAlert-icon": {
                  color: isSmallScreen
                    ? theme.palette.warning.main
                    : theme.palette.warning.light,
                },
              }}
            >
              {noRulesError}
            </Alert>
          )}

          <StyledMultiSelect
            label={t("functionalities.form.companyRulesLabel")}
            value={selectedRuleIds}
            onChange={onSelectedRuleIdsChange}
            options={companyRules.map((rule) => ({
              label: rule.title,
              value: rule.id,
            }))}
            placeholder={
              rulesLoading
                ? t("functionalities.form.rulesLoading")
                : companyRules.length === 0
                ? t("functionalities.form.noRulesAvailable")
                : t("functionalities.form.selectRulesPlaceholder")
            }
            disabled={
              isProcessing ||
              rulesLoading ||
              companyRules.length === 0
            }
            mode={isSmallScreen ? "light" : "dark"}
          />
          {selectedRuleIds.length > 0 && (
            <Box mt={1}>
              <Typography variant="caption" color={bodyColor}>
                {t("functionalities.form.rulesSelected", {
                  count: selectedRuleIds.length,
                })}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {isSmallScreen && (
        <Box display={"flex"} flexDirection={"column"} gap={1}>
          <Typography variant="h5">{t("common.upload")}</Typography>
          <Button
            size="large"
            variant="outlined"
            color="primary"
            onClick={onOpenBottomDrawer}
            endIcon={
              <Icon
                icon="hugeicons:arrow-up-right-01"
                width={24}
                height={24}
                color={theme.palette.primary.main}
              />
            }
          >
            {t("functionalities.form.availableSpec")}
          </Button>
        </Box>
      )}

      <StyledTextArea
        placeholder={t("functionalities.form.customInstructionsPlaceholder")}
        rows={3}
        label={t("functionalities.form.customInstructionsLabel")}
        value={customInstructions}
        onChange={(e) => onCustomInstructionsChange(e.target.value)}
        disabled={isProcessing}
      />

      {/* Document Display - Always allow multiple document selection */}
      <MultipleFilesSection
        multipleFiles={multipleFiles}
        multipleSelectedDocuments={multipleSelectedDocuments}
        onFileRemove={onFileRemove}
        onDocumentDeselect={onDocumentDeselect}
        onFileUpload={onFileUpload}
        onClearAll={onClearAll}
        onDownloadDocument={onDownloadDocument}
        isProcessing={isProcessing}
        isSmallScreen={isSmallScreen}
        maxFiles={5}
      />
    </>
  );
};

export default CompanyStandardForm;

